// Golden Ring Avatar Component - Visual ring indicator only
// Level 1: 50% ring with subtle glow
// Level 2: 100% ring with nice glow
// Level 3: 100% ring with IMPRESSIVE sparkles, particles, and shine animation

import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

export const GoldenRingAvatar = ({ 
  user, 
  size = "md", // sm, md, lg, xl
  showRing = true,
  className = "",
  onClick
}) => {
  const goldenLevel = user?.golden?.level || 0;
  
  // Size configurations
  const sizes = {
    sm: { container: 52, avatar: "w-10 h-10", stroke: 4, sparkleSize: 14 },
    md: { container: 72, avatar: "w-14 h-14", stroke: 5, sparkleSize: 18 },
    lg: { container: 96, avatar: "w-20 h-20", stroke: 6, sparkleSize: 22 },
    xl: { container: 136, avatar: "w-28 h-28", stroke: 7, sparkleSize: 26 },
  };
  
  const config = sizes[size] || sizes.md;
  const ringSize = config.container;
  const ringRadius = (ringSize / 2) - (config.stroke / 2) - 2;
  const circumference = 2 * Math.PI * ringRadius;
  
  // Calculate ring fill based on level
  const getRingDash = () => {
    if (goldenLevel === 0) return `0 ${circumference}`;
    if (goldenLevel === 1) return `${circumference * 0.5} ${circumference * 0.5}`; // 50%
    return `${circumference} 0`; // 100% for level 2 and 3
  };
  
  const getImageUrl = (url) => {
    if (!url) return '/images/default-avatar.png';
    if (url === '/images/default-avatar.png') return url;
    return url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  };

  // Generate unique ID for gradients
  const uniqueId = `golden-${size}-${Math.random().toString(36).substr(2, 9)}`;

  // Get ring color based on tier (NEW system: bronze/silver/gold/gold_plus)
  const tier = user?.golden?.tier;
  
  const getRingColors = () => {
    if (tier === "bronze") return { start: "#CD7F32", mid: "#E8A95C", end: "#B87333" };
    if (tier === "silver") return { start: "#C0C0C0", mid: "#E8E8E8", end: "#A9A9A9" };
    if (tier === "gold_plus") return { start: "#FFD700", mid: "#FFFACD", end: "#FFA500" };
    // Default gold
    return { start: "#FFD700", mid: "#FFEC8B", end: "#DAA520" };
  };
  const ringColors = getRingColors();

  // Get glow intensity based on level
  const getGlowStyle = () => {
    if (tier === "bronze") return 'drop-shadow(0 0 8px rgba(205,127,50,0.7)) drop-shadow(0 0 16px rgba(205,127,50,0.3))';
    if (tier === "silver") return 'drop-shadow(0 0 8px rgba(192,192,192,0.8)) drop-shadow(0 0 16px rgba(192,192,192,0.4))';
    if (tier === "gold_plus") return 'drop-shadow(0 0 15px rgba(255,215,0,1)) drop-shadow(0 0 30px rgba(255,180,0,0.8)) drop-shadow(0 0 45px rgba(255,150,0,0.5))';
    if (goldenLevel >= 3) return 'drop-shadow(0 0 12px rgba(255,215,0,0.9)) drop-shadow(0 0 25px rgba(255,180,0,0.6))';
    if (goldenLevel >= 2) return 'drop-shadow(0 0 10px rgba(255,215,0,0.8)) drop-shadow(0 0 20px rgba(255,180,0,0.5))';
    if (goldenLevel >= 1) return 'drop-shadow(0 0 6px rgba(255,215,0,0.7)) drop-shadow(0 0 12px rgba(255,180,0,0.4))';
    return '';
  };

  // No golden ring - just show avatar
  if (goldenLevel === 0 || !showRing) {
    return (
      <div 
        className={`inline-flex items-center justify-center ${className}`}
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        <Avatar className={config.avatar}>
          <AvatarImage src={getImageUrl(user?.picture)} alt={user?.name} className="object-cover" />
          <AvatarFallback className="bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100">
            <img src="/images/default-avatar.png" alt="" className="w-full h-full object-cover" />
          </AvatarFallback>
        </Avatar>
      </div>
    );
  }
  
  // With golden ring
  return (
    <div 
      className={`relative inline-flex items-center justify-center ${className}`}
      onClick={onClick}
      style={{ 
        cursor: onClick ? 'pointer' : 'default',
        width: ringSize,
        height: ringSize,
      }}
    >
      {/* Outer glow: animated pulse ONLY for gold_plus; static warm halo for gold */}
      {tier === "gold_plus" && (
        <div 
          className="absolute inset-0 rounded-full animate-pulse"
          style={{
            background: `radial-gradient(circle, rgba(255,215,0,0.5) 0%, rgba(255,180,0,0.3) 50%, transparent 70%)`,
            transform: 'scale(1.3)',
            animationDuration: '1.5s'
          }}
        />
      )}
      {goldenLevel >= 3 && tier !== "gold_plus" && (
        <div 
          className="absolute inset-0 rounded-full"
          style={{
            background: `radial-gradient(circle, rgba(255,215,0,0.35) 0%, rgba(255,180,0,0.18) 50%, transparent 70%)`,
            transform: 'scale(1.25)'
          }}
        />
      )}
      
      {/* Golden Ring SVG */}
      <svg 
        className="absolute top-0 left-0"
        width={ringSize} 
        height={ringSize}
        style={{ 
          transform: 'rotate(-90deg)',
          filter: getGlowStyle()
        }}
      >
        <defs>
          {/* Gold gradient - color based on tier */}
          <linearGradient id={`gold-grad-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={ringColors.start} />
            <stop offset="20%" stopColor={ringColors.mid} />
            <stop offset="40%" stopColor={ringColors.start} />
            <stop offset="60%" stopColor={ringColors.mid} />
            <stop offset="80%" stopColor={ringColors.end} />
            <stop offset="100%" stopColor={ringColors.start} />
          </linearGradient>
          
          {/* Shimmer gradient: EXCLUSIVE to gold_plus */}
          {tier === "gold_plus" && (
            <linearGradient id={`shimmer-${uniqueId}`} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FFD700">
                <animate attributeName="stop-color" values="#FFD700;#FFFFFF;#FFD700" dur="1s" repeatCount="indefinite" />
              </stop>
              <stop offset="25%" stopColor="#FFF8DC">
                <animate attributeName="stop-color" values="#FFF8DC;#FFFFFF;#FFD700;#FFF8DC" dur="1s" repeatCount="indefinite" />
              </stop>
              <stop offset="50%" stopColor="#FFFFFF">
                <animate attributeName="stop-color" values="#FFFFFF;#FFD700;#FFFFFF" dur="1s" repeatCount="indefinite" />
              </stop>
              <stop offset="75%" stopColor="#FFF8DC">
                <animate attributeName="stop-color" values="#FFF8DC;#FFD700;#FFFFFF;#FFF8DC" dur="1s" repeatCount="indefinite" />
              </stop>
              <stop offset="100%" stopColor="#FFD700">
                <animate attributeName="stop-color" values="#FFD700;#FFFFFF;#FFD700" dur="1s" repeatCount="indefinite" />
              </stop>
            </linearGradient>
          )}
        </defs>
        
        {/* Background ring (subtle gray) - more visible for level 1 */}
        <circle
          cx={ringSize / 2}
          cy={ringSize / 2}
          r={ringRadius}
          fill="none"
          stroke={goldenLevel === 1 ? "#D4AF37" : "#E5E7EB"}
          strokeWidth={config.stroke}
          opacity={goldenLevel === 1 ? "0.3" : "0.4"}
        />
        
        {/* Golden ring */}
        <circle
          cx={ringSize / 2}
          cy={ringSize / 2}
          r={ringRadius}
          fill="none"
          stroke={tier === "gold_plus" ? `url(#shimmer-${uniqueId})` : `url(#gold-grad-${uniqueId})`}
          strokeWidth={config.stroke}
          strokeDasharray={getRingDash()}
          strokeLinecap="round"
        >
          {/* Rotation animation: EXCLUSIVE to gold_plus */}
          {tier === "gold_plus" && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              from={`0 ${ringSize/2} ${ringSize/2}`}
              to={`360 ${ringSize/2} ${ringSize/2}`}
              dur="4s"
              repeatCount="indefinite"
            />
          )}
        </circle>
      </svg>
      
      {/* Avatar - centered */}
      <Avatar className={`${config.avatar} absolute z-10`}>
        <AvatarImage src={getImageUrl(user?.picture)} alt={user?.name} className="object-cover" />
        <AvatarFallback className="bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100">
          <img src="/images/default-avatar.png" alt="" className="w-full h-full object-cover" />
        </AvatarFallback>
      </Avatar>
      
      {/* Level 1 - Subtle sparkle */}
      {goldenLevel === 1 && (
        <div className="absolute -top-0.5 -right-0.5 z-20">
          <div 
            className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"
            style={{ 
              boxShadow: '0 0 6px rgba(255, 215, 0, 0.8)',
              animationDuration: '1.5s'
            }}
          />
        </div>
      )}
      
      {/* Animated stars: EXCLUSIVE to Gold Plus */}
      {tier === "gold_plus" && (
        <>
          {/* Main sparkle - top right */}
          <div className="absolute -top-2 -right-2 z-20" style={{ animation: 'pulse 0.8s ease-in-out infinite' }}>
            <svg width={config.sparkleSize} height={config.sparkleSize} viewBox="0 0 24 24">
              <path 
                d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10Z"
                fill="#FFD700"
                style={{ filter: 'drop-shadow(0 0 6px rgba(255, 215, 0, 1)) drop-shadow(0 0 12px rgba(255, 255, 255, 0.8))' }}
              />
            </svg>
          </div>
          
          {/* Sparkle - top left */}
          <div className="absolute -top-1 -left-1 z-20" style={{ animation: 'pulse 1.2s ease-in-out infinite', animationDelay: '0.3s' }}>
            <svg width={config.sparkleSize * 0.7} height={config.sparkleSize * 0.7} viewBox="0 0 24 24">
              <path 
                d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10Z"
                fill="#FFFFFF"
                style={{ filter: 'drop-shadow(0 0 4px rgba(255, 215, 0, 1))' }}
              />
            </svg>
          </div>
          
          {/* Sparkle - bottom */}
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-20" style={{ animation: 'pulse 1s ease-in-out infinite', animationDelay: '0.6s' }}>
            <svg width={config.sparkleSize * 0.8} height={config.sparkleSize * 0.8} viewBox="0 0 24 24">
              <path 
                d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10Z"
                fill="#FFD700"
                style={{ filter: 'drop-shadow(0 0 5px rgba(255, 215, 0, 1))' }}
              />
            </svg>
          </div>
          
          {/* Sparkle - right */}
          <div className="absolute top-1/2 -right-2 -translate-y-1/2 z-20" style={{ animation: 'pulse 1.4s ease-in-out infinite', animationDelay: '0.2s' }}>
            <svg width={config.sparkleSize * 0.6} height={config.sparkleSize * 0.6} viewBox="0 0 24 24">
              <path 
                d="M12 0L14 10L24 12L14 14L12 24L10 14L0 12L10 10Z"
                fill="#FFFFFF"
                style={{ filter: 'drop-shadow(0 0 4px rgba(255, 215, 0, 1))' }}
              />
            </svg>
          </div>
          
          {/* Floating particles */}
          <div className="absolute inset-0 z-0 overflow-visible pointer-events-none">
            <div 
              className="absolute w-1.5 h-1.5 bg-yellow-300 rounded-full"
              style={{ 
                top: '10%', 
                right: '5%',
                animation: 'float 2s ease-in-out infinite',
                boxShadow: '0 0 4px rgba(255, 215, 0, 0.8)'
              }}
            />
            <div 
              className="absolute w-1 h-1 bg-yellow-200 rounded-full"
              style={{ 
                bottom: '15%', 
                left: '10%',
                animation: 'float 2.5s ease-in-out infinite',
                animationDelay: '0.5s',
                boxShadow: '0 0 3px rgba(255, 215, 0, 0.8)'
              }}
            />
            <div 
              className="absolute w-1 h-1 bg-white rounded-full"
              style={{ 
                top: '40%', 
                left: '0%',
                animation: 'float 1.8s ease-in-out infinite',
                animationDelay: '1s',
                boxShadow: '0 0 3px rgba(255, 255, 255, 0.8)'
              }}
            />
          </div>
        </>
      )}
      
      {/* CSS for float animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); opacity: 1; }
          50% { transform: translateY(-8px) scale(1.2); opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

export default GoldenRingAvatar;
